/* eslint-disable prettier/prettier */
import { useCallback, useState } from 'react';

import {
	assessmentSubmissionService,
	CourseSubmission,
	CourseSubmissionFilters,
	CourseSubmissionsResult,
} from '../services/courseAssessmentSubmission.service';

interface UseCourseSubmissionsReturn {
	submissions: CourseSubmission[];
	total: number;
	page: number;
	limit: number;
	loading: boolean;
	error: string | null;

	fetchSubmissions: (
		customFilters?: CourseSubmissionFilters
	) => Promise<void>;

	refetch: () => Promise<void>;
}

export const useCourseSubmissions = (
	courseId?: string
): UseCourseSubmissionsReturn => {
	const [submissions, setSubmissions] = useState<CourseSubmission[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(20);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchSubmissions = useCallback(
		async (customFilters: CourseSubmissionFilters = {}) => {
			if (!courseId) {
				console.error(
					'❌ Course ID is missing'
				);
				setError('Course ID is required.');
				return;
			}

			try {
				setLoading(true);
				setError(null);

				const filters: CourseSubmissionFilters = {
					page: customFilters.page ?? 1,
					limit: customFilters.limit ?? 20,
					assessmentId: customFilters.assessmentId,
					learnerId: customFilters.learnerId,
					status: customFilters.status,
				};

				const response: CourseSubmissionsResult =
					await assessmentSubmissionService.getCourseSubmissions(
						courseId,
						filters
					);

				setSubmissions(
					response?.submissions ?? []
				);

				setTotal(response?.total ?? 0);
				setPage(response?.page ?? filters.page ?? 1);
				setLimit(
					response?.limit ?? filters.limit ?? 20
				);
			} catch (err: any) {
				console.error(
					'❌ fetchSubmissions failed:',
					err
				);

				setSubmissions([]);
				setTotal(0);

				setError(
					err?.response?.data?.message ||
						err?.message ||
						'Failed to load learner submissions.'
				);
			} finally {
				setLoading(false);
			}
		},
		[courseId]
	);

	const refetch = useCallback(async () => {
		await fetchSubmissions({
			page,
			limit,
		});
	}, [fetchSubmissions, page, limit]);

	return {
		submissions,
		total,
		page,
		limit,
		loading,
		error,
		fetchSubmissions,
		refetch,
	};
};